import type { MarketingPreferencesData } from '../../domain/types'
import type { InvisibleRecaptchaHandle } from '@/scopes/common/components/InvisibleRecaptcha/InvisibleRecaptcha'
import { mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, IndeterminateProgressCircle, TextField, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'
import { InvisibleRecaptcha } from '@/scopes/common/components/InvisibleRecaptcha/InvisibleRecaptcha'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import marketingPreferencesUseCases from '../../domain/useCases'
import { useMarketingPreferencesStore } from '../../infrastructure/useMarketingPreferencesStore'
import { ControlledCheckboxField } from './ControlledCheckboxField'

interface MarketingPreferencesFormProps {
  useCases: ReturnType<typeof marketingPreferencesUseCases>
}

const FormWrapper = styled.div`
  position: relative;
`

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Form = styled.form<{ $disabled?: boolean }>`
  position: relative;
  background-color: ${tok('semantics_views_background_color')};
  padding: ${tokPx('primitives_space_16')};
  max-width: ${tokPx('primitives_area_size_800')};
  margin-bottom: ${tokPx('primitives_space_32')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding: ${tokPx('primitives_space_24')};
    margin-bottom: ${tokPx('primitives_space_64')};
    border-radius: ${tokPx('semantics_boxes_corner_radius')};
  }

  ${({ $disabled }) =>
    $disabled
    && css`
      opacity: ${tok('primitives_opacity_disabled')}%;
      pointer-events: none;
    `}
`

const TypographyStyled = styled(Typography)`
  margin-top: ${tokPx('primitives_space_12')};
  margin-bottom: ${tokPx('primitives_space_20')};
`

const FormSection = styled.div`
  border-radius: ${tokPx('semantics_boxes_corner_radius')};
  background-color: ${tok('semantics_boxes_background_color')};
  border: ${tokPx('semantics_boxes_border_width')} solid ${tok('semantics_boxes_border_color')};
  padding: ${tokPx('semantics_boxes_padding')};
  margin-top: ${tokPx('primitives_space_12')};
`

const MaxWidthContainer = styled.div`
  max-width: ${tokPx('primitives_area_size_480')};
  width: 100%;
`

const FormFieldsContainer = styled(MaxWidthContainer)`
  display: flex;
  gap: ${tokPx('semantics_boxes_padding')};
  flex-direction: column;
  align-items: stretch;
`

const RemarkTypography = styled(Typography)`
  margin: ${tokPx('primitives_space_12')} 0;
`

const SubmitButton = styled(Button)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: auto;
  }
`

const ErrorMessage = styled(Typography)`
  color: ${tok('semantics_input_fields_invalid_icon_color')};
  margin-top: ${tokPx('primitives_space_2')};
`

export const MarketingPreferencesForm: React.FC<MarketingPreferencesFormProps> = ({ useCases }) => {
  const privacyLink = useConfiguration('links.privacy')
  const intl = useIntl()
  const [hasSubmitted, setHasSubmitted] = React.useState(false)
  const recaptchaRef = React.useRef<InvisibleRecaptchaHandle>(null)
  const isSaving = useMarketingPreferencesStore(state => state.savePreferences.loading)
  const isSuccess = useMarketingPreferencesStore(state => state.savePreferences.success)

  const { control, handleSubmit, register, formState: { errors }, setError, clearErrors, getValues, reset } = useForm<MarketingPreferencesData>({
    defaultValues: {
      emailAddress: '',
      unsubscribeChannels: {
        email: false,
        smsWhatsapp: false,
        post: false,
        myAccount: false,
      },
      personalization: false,
      allBrands: false,
    },
  })

  React.useEffect(() => {
    if (isSuccess) {
      reset()
    }
  }, [isSuccess, reset])

  const validateCheckboxGroup = () => {
    const values = getValues()
    const noneSelected = !values.unsubscribeChannels.email
      && !values.unsubscribeChannels.smsWhatsapp
      && !values.unsubscribeChannels.post
      && !values.unsubscribeChannels.myAccount

    if (noneSelected) {
      setError('unsubscribeChannels', {
        type: 'manual',
        message: intl.formatMessage({ id: 'marketing_preferences_form_field_group_error' }),
      })
    }
    else {
      clearErrors('unsubscribeChannels')
    }
  }

  const handleCheckboxGroupValidation = () => {
    if (!hasSubmitted)
      return
    validateCheckboxGroup()
  }

  const onSubmit = async (data: MarketingPreferencesData) => {
    const recaptchaToken = await recaptchaRef.current?.execute()

    useCases.updatePreferences({
      ...data,
      recaptchaToken,
    })
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setHasSubmitted(true)
    validateCheckboxGroup()
    handleSubmit(onSubmit)(e)
  }

  return (
    <FormWrapper>
      <Form onSubmit={handleFormSubmit} $disabled={isSaving}>
        <InvisibleRecaptcha ref={recaptchaRef} />
        <Typography forwardedAs="h2" variant="display-g-regular">
          {intl.formatMessage({ id: 'marketing_preferences_form_title' })}
        </Typography>
        <MaxWidthContainer>
          <TypographyStyled forwardedAs="p" variant="body-m-bold-snug">
            {intl.formatMessage({ id: 'marketing_preferences_form_description_1' })}
          </TypographyStyled>
          <Typography forwardedAs="p" variant="body-m-bold-snug">
            {intl.formatMessage({ id: 'marketing_preferences_form_description_2' })}
          </Typography>
          <Typography forwardedAs="p" variant="body-m-regular-snug">
            {intl.formatMessage({ id: 'marketing_preferences_form_description_3' })}
          </Typography>
        </MaxWidthContainer>
        <FormSection>
          <FormFieldsContainer>
            <TextField
              label={intl.formatMessage({ id: 'marketing_preferences_form_field_label_email' })}
              {...register('emailAddress', {
                required: intl.formatMessage({ id: 'marketing_preferences_form_field_email_required' }),
                pattern: {
                  value: /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/,
                  message: intl.formatMessage({ id: 'marketing_preferences_form_field_email_invalid' }),
                },
              })}
              feedbackText={errors.emailAddress?.message}
              status={errors.emailAddress ? 'error' : undefined}
            />
            <Typography forwardedAs="p" variant="body-m-bold-snug">
              {intl.formatMessage({ id: 'marketing_preferences_form_field_label_unsubscribe_channels' })}
            </Typography>
            <ControlledCheckboxField
              name="unsubscribeChannels.email"
              control={control}
              label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_email' })}
              handleCheckboxGroupValidation={handleCheckboxGroupValidation}
            />
            <ControlledCheckboxField
              name="unsubscribeChannels.smsWhatsapp"
              control={control}
              label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_sms' })}
              handleCheckboxGroupValidation={handleCheckboxGroupValidation}
            />
            <ControlledCheckboxField
              name="unsubscribeChannels.post"
              control={control}
              label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_post' })}
              handleCheckboxGroupValidation={handleCheckboxGroupValidation}
            />
            <div>
              <ControlledCheckboxField
                name="unsubscribeChannels.myAccount"
                control={control}
                label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_my_sunweb' })}
                handleCheckboxGroupValidation={handleCheckboxGroupValidation}
              />
              {errors.unsubscribeChannels && (
                <ErrorMessage forwardedAs="p" variant="body-s-regular-tight">
                  {errors.unsubscribeChannels.message}
                </ErrorMessage>
              )}
            </div>

            <Typography forwardedAs="p" variant="body-m-bold-snug">
              {intl.formatMessage({ id: 'marketing_preferences_form_field_label_unsubscribe_others' })}
            </Typography>
            <ControlledCheckboxField
              name="personalization"
              control={control}
              label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_personalization' })}
            />
            <ControlledCheckboxField
              name="allBrands"
              control={control}
              label={
                intl.formatMessage(
                  {
                    id: 'marketing_preferences_form_field_checkbox_all_brands',
                  },
                  {
                    privacyLink: (
                      <a href={privacyLink} target="_blank" rel="noopener noreferrer">
                        {intl.formatMessage({ id: 'marketing_preferences_privacy_link' })}
                      </a>
                    ),
                  },
                )
              }
            />
          </FormFieldsContainer>
        </FormSection>
        <MaxWidthContainer>
          <RemarkTypography forwardedAs="p" variant="body-s-regular-snug">
            {intl.formatMessage({ id: 'marketing_preferences_form_field_label_unsubscribe_remark' })}
          </RemarkTypography>
          <SubmitButton type="submit" variant="primary" fullWidth>
            {intl.formatMessage({ id: 'marketing_preferences_form_submit_button' })}
          </SubmitButton>
        </MaxWidthContainer>
      </Form>
      {isSaving
      && (
        <LoaderOverlay>
          <IndeterminateProgressCircle />
        </LoaderOverlay>
      )}
    </FormWrapper>
  )
}
